import { useState, useMemo, useRef, useEffect } from 'react'
import { ArrowDownLeft, ArrowUpRight, Play, Square, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronUp, MessageSquarePlus, Loader2 } from 'lucide-react'
import type { CallRecord, PaginationInfo } from '../types'
import { RESULT_STYLES, getScoreColor } from '../types'
import { cn } from '@/lib/utils'
import { AudioPlayer } from './AudioPlayer'

interface PlayingRecording {
  sessionId: string
  csrName: string
}

type SortField = 'time' | 'csrName' | 'score' | null
type SortDirection = 'asc' | 'desc'

interface CallLogTableProps {
  calls: CallRecord[]
  pagination: PaginationInfo
  onPageChange: (page: number) => void
  onItemsPerPageChange: (count: number) => void
  onNoteUpdate?: (telephonySessionId: string, notes: string) => void
}

interface SortableHeaderProps {
  label: string
  field: SortField
  currentSort: SortField
  direction: SortDirection
  onSort: (field: SortField) => void
  className?: string
}

function SortableHeader({ label, field, currentSort, direction, onSort, className }: SortableHeaderProps) {
  const isActive = currentSort === field

  return (
    <th
      className={cn(
        'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none',
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
          )
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
        )}
      </div>
    </th>
  )
}

// Clean name from phone number suffix (e.g., "Greenwood Appliance Repair - 16785779680" → "Greenwood Appliance Repair")
// Returns "Unknown" if name is empty/null or looks like a queue name
function cleanName(name: string | null | undefined): string {
  if (!name) return 'Unknown'
  const cleaned = name.replace(/\s*-\s*\d+$/, '').trim()
  return cleaned || 'Unknown'
}

// Check if call has a meaningful summary that's worth displaying/expanding
function hasMeaningfulSummary(call: CallRecord): boolean {
  const summary = call.summary?.trim().toLowerCase() || ''

  // No summary or placeholder text
  if (!summary) return false
  if (summary === 'no summary available') return false
  if (summary === 'no summary') return false

  // Missed calls typically don't have useful summaries
  if (call.result === 'missed') return false

  return true
}

// Generate page numbers with ellipsis for large page counts
function generatePageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: (number | 'ellipsis')[] = []

  // Always show first page
  pages.push(1)

  if (currentPage > 3) {
    pages.push('ellipsis')
  }

  // Show pages around current
  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis')
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages)
  }

  return pages
}

// Labels for scoring breakdown categories
const CATEGORY_LABELS: Record<string, string> = {
  greeting: 'Greeting',
  qualification: 'Qualification',
  control: 'Control',
  closing: 'Closing',
  confidence: 'Confidence',
  accuracy: 'Accuracy',
  assistance: 'Assistance',
  closure: 'Closure',
}

// Max scores per route
const ROUTE_A_MAX: Record<string, number> = {
  greeting: 10, qualification: 20, control: 25, closing: 30, confidence: 15,
}
const ROUTE_B_MAX: Record<string, number> = {
  greeting: 10, assistance: 20, accuracy: 20, control: 20, closure: 20, confidence: 10,
}

function getCategoryMax(key: string, route?: string | null): number {
  if (route === 'B') return ROUTE_B_MAX[key] ?? 100
  return ROUTE_A_MAX[key] ?? ROUTE_B_MAX[key] ?? 100
}

interface ScoreBreakdownPopupProps {
  breakdown: Record<string, number>
  totalScore: number
  route?: string | null
  onClose: () => void
}

function ScoreBreakdownPopup({ breakdown, totalScore, route, onClose }: ScoreBreakdownPopupProps) {
  const categories = Object.entries(breakdown).filter(([key]) => key !== 'operator_score')

  return (
    <div className="absolute right-0 top-full z-50 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg w-64">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-900">Score Breakdown</span>
        <span className={cn('text-sm font-bold px-2 py-0.5 rounded-full', getScoreColor(totalScore))}>
          {totalScore}
        </span>
      </div>
      <div className="space-y-1.5">
        {categories.map(([key, value]) => {
          const label = CATEGORY_LABELS[key] || key
          const max = getCategoryMax(key, route)
          const pct = Math.min(100, (value / max) * 100)
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">{label}</span>
                <span className="font-medium text-gray-900">{value}/{max}</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full mt-0.5">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 text-xs text-gray-500 hover:text-gray-700"
      >
        Close
      </button>
    </div>
  )
}

// Get CSRF token from meta tag
function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

interface NoteCellProps {
  note: string | null | undefined
  telephonySessionId: string | null | undefined
  onNoteUpdate?: (telephonySessionId: string, notes: string) => void
}

function NoteCell({ note, telephonySessionId, onNoteUpdate }: NoteCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(note || '')
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    if (!telephonySessionId) return
    const trimmed = value.trim()
    if (trimmed === (note || '')) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      const response = await fetch(`/dashboard/calls/${encodeURIComponent(telephonySessionId)}/notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({ notes: trimmed }),
      })

      if (response.ok) {
        onNoteUpdate?.(telephonySessionId, trimmed)
      }
    } catch (err) {
      console.error('Failed to save note:', err)
    } finally {
      setIsSaving(false)
      setIsEditing(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      setValue(note || '')
      setIsEditing(false)
    }
  }

  if (isSaving) {
    return (
      <td className="px-4 py-3">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </td>
    )
  }

  if (isEditing) {
    return (
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          rows={2}
          className="w-full min-w-[180px] text-sm border border-blue-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Add a note..."
        />
      </td>
    )
  }

  return (
    <td className="px-4 py-3">
      {note ? (
        <div
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors max-w-[200px]"
          onClick={(e) => { e.stopPropagation(); setIsEditing(true) }}
          title={note}
        >
          <MessageSquarePlus className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-800 truncate">{note}</span>
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIsEditing(true) }}
          className="p-1 text-gray-300 hover:text-blue-500 transition-colors"
          title="Add note"
        >
          <MessageSquarePlus className="w-4 h-4" />
        </button>
      )}
    </td>
  )
}

export function CallLogTable({ calls, pagination, onPageChange, onItemsPerPageChange, onNoteUpdate }: CallLogTableProps) {
  const { currentPage, totalPages, totalItems, itemsPerPage } = pagination
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const [sortField, setSortField] = useState<SortField>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null)
  const [scoreExpandedRowId, setScoreExpandedRowId] = useState<number | null>(null)
  const [playingRecording, setPlayingRecording] = useState<PlayingRecording | null>(null)

  const toggleExpand = (id: number) => {
    setExpandedRowId(prev => prev === id ? null : id)
  }

  const toggleScoreBreakdown = (id: number) => {
    setScoreExpandedRowId(prev => prev === id ? null : id)
  }

  const handlePlayRecording = (sessionId: string | null | undefined, csrName: string) => {
    if (!sessionId) return

    if (playingRecording?.sessionId === sessionId) {
      // Stop playing - AudioPlayer will handle its own cleanup
      setPlayingRecording(null)
    } else {
      setPlayingRecording({ sessionId, csrName })
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortField(null)
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedCalls = useMemo(() => {
    if (!sortField) return calls

    return [...calls].sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'time':
          // Parse time like "10:42 AM" to compare
          const parseTime = (timeStr: string) => {
            const [time, period] = timeStr.split(' ')
            const [hours, minutes] = time.split(':').map(Number)
            let h = hours
            if (period === 'PM' && hours !== 12) h += 12
            if (period === 'AM' && hours === 12) h = 0
            return h * 60 + minutes
          }
          comparison = parseTime(a.time) - parseTime(b.time)
          break
        case 'csrName':
          comparison = a.csrName.localeCompare(b.csrName)
          break
        case 'score':
          comparison = a.score - b.score
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [calls, sortField, sortDirection])

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Audio Player */}
      {playingRecording && (
        <AudioPlayer
          sessionId={playingRecording.sessionId}
          csrName={playingRecording.csrName}
          onClose={() => setPlayingRecording(null)}
        />
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <SortableHeader
                label="Time"
                field="time"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">
                From
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                To
              </th>
              <SortableHeader
                label="CSR"
                field="csrName"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Result
              </th>
              <SortableHeader
                label="Score"
                field="score"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
                className="text-center"
              />
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Rec
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Summary
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">
                Note
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedCalls.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No calls found matching your filters
                </td>
              </tr>
            ) : (
              sortedCalls.map((call) => (
                <CallRow
                  key={call.id}
                  call={call}
                  isExpanded={expandedRowId === call.id}
                  isPlaying={playingRecording?.sessionId === call.telephonySessionId}
                  isScoreExpanded={scoreExpandedRowId === call.id}
                  onToggleExpand={() => toggleExpand(call.id)}
                  onTogglePlay={() => handlePlayRecording(call.telephonySessionId, call.csrName)}
                  onToggleScoreBreakdown={() => toggleScoreBreakdown(call.id)}
                  onNoteUpdate={onNoteUpdate}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        {/* Left: Items per page selector + info */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md pl-2 pr-7 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>
          <p className="text-sm text-gray-600">
            {totalItems === 0 ? (
              'No results'
            ) : (
              <>
                Showing <span className="font-medium">{startItem}</span> to{' '}
                <span className="font-medium">{endItem}</span> of{' '}
                <span className="font-medium">{totalItems}</span>
              </>
            )}
          </p>
        </div>

        {/* Right: Page numbers */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={cn(
              'p-1.5 rounded-md border transition-colors',
              currentPage === 1
                ? 'text-gray-300 border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            )}
            title="First page"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>

          {/* Previous page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={cn(
              'p-1.5 rounded-md border transition-colors',
              currentPage === 1
                ? 'text-gray-300 border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            )}
            title="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Page numbers */}
          {generatePageNumbers(currentPage, totalPages).map((page, index) =>
            page === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={cn(
                  'min-w-[32px] h-8 px-2 text-sm font-medium rounded-md border transition-colors',
                  page === currentPage
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'text-gray-700 border-gray-200 bg-white hover:bg-gray-50'
                )}
              >
                {page}
              </button>
            )
          )}

          {/* Next page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={cn(
              'p-1.5 rounded-md border transition-colors',
              currentPage === totalPages
                ? 'text-gray-300 border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            )}
            title="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Last page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={cn(
              'p-1.5 rounded-md border transition-colors',
              currentPage === totalPages
                ? 'text-gray-300 border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            )}
            title="Last page"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

interface CallRowProps {
  call: CallRecord
  isExpanded: boolean
  isPlaying: boolean
  isScoreExpanded: boolean
  onToggleExpand: () => void
  onTogglePlay: () => void
  onToggleScoreBreakdown?: () => void
  onNoteUpdate?: (telephonySessionId: string, notes: string) => void
}

function CallRow({ call, isExpanded, isPlaying, isScoreExpanded, onToggleExpand, onTogglePlay, onToggleScoreBreakdown, onNoteUpdate }: CallRowProps) {
  const resultStyle = RESULT_STYLES[call.result]

  // Format duration as mm:ss
  const formatDuration = (seconds: number | undefined) => {
    if (!seconds || seconds === 0) return 'Missed'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Time + Direction + Duration */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{call.time}</span>
          {call.direction === 'inbound' ? (
            <ArrowDownLeft className="w-4 h-4 text-emerald-500" />
          ) : (
            <ArrowUpRight className="w-4 h-4 text-blue-500" />
          )}
          <span className="text-xs text-gray-400">{formatDuration(call.durationSec)}</span>
        </div>
      </td>

      {/* From (Company/Name + Phone) */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          {call.fromCompany ? (
            <>
              <span className="text-sm text-gray-900">{call.fromCompany}</span>
              <span className="text-xs text-gray-500">{call.fromPhone}</span>
            </>
          ) : call.direction === 'inbound' ? (
            <>
              <span className="text-sm text-gray-900">{cleanName(call.fromName)}</span>
              {call.fromPhone && <span className="text-xs text-gray-500">{call.fromPhone}</span>}
            </>
          ) : (
            <span className="text-sm text-gray-900">{call.fromPhone || 'N/A'}</span>
          )}
        </div>
      </td>

      {/* To (Company + Phone) */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          {call.toCompany ? (
            <>
              <span className="text-sm text-gray-900">{call.toCompany}</span>
              <span className="text-xs text-gray-500">{call.toPhone}</span>
            </>
          ) : (
            <span className="text-sm text-gray-900">{call.toPhone || 'N/A'}</span>
          )}
        </div>
      </td>

      {/* CSR */}
      <td className="px-4 py-3">
        <span className="text-sm text-gray-900">{call.csrName}</span>
      </td>

      {/* Result */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'px-2 py-0.5 rounded text-xs font-medium',
              resultStyle.bg,
              resultStyle.text
            )}
          >
            {resultStyle.label}
          </span>
          {call.leadType === 'new' && call.result === 'booked' && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
              New
            </span>
          )}
          {/* SubCategory tag for existing, not_relevant and business */}
          {call.subCategory && (call.result === 'existing' || call.result === 'not_relevant' || call.result === 'business') && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
              {call.subCategory}
            </span>
          )}
          {call.isNewOpportunity && call.result !== 'booked' && (
            <span className="text-amber-500">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </span>
          )}
        </div>
      </td>

      {/* Score */}
      <td className="px-4 py-3 text-center relative">
        {call.score > 0 ? (
          <>
            <span
              onClick={(e) => { e.stopPropagation(); onToggleScoreBreakdown?.() }}
              className={cn(
                'inline-flex items-center justify-center w-10 h-7 rounded-full text-sm font-bold',
                getScoreColor(call.score),
                call.scoringBreakdown && 'cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all'
              )}
            >
              {call.score}
            </span>
            {isScoreExpanded && call.scoringBreakdown && (
              <ScoreBreakdownPopup
                breakdown={call.scoringBreakdown}
                totalScore={call.score}
                route={call.route}
                onClose={() => onToggleScoreBreakdown?.()}
              />
            )}
          </>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      {/* Recording */}
      <td className="px-4 py-3 text-center">
        {call.hasRecording && (
          <button
            type="button"
            onClick={onTogglePlay}
            className={cn(
              'p-1.5 rounded-full transition-colors',
              isPlaying
                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {isPlaying ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
        )}
      </td>

      {/* Summary */}
      <td className="px-4 py-3 relative">
        {hasMeaningfulSummary(call) ? (
          <>
            <div
              className="flex items-center gap-1 cursor-pointer group"
              onClick={onToggleExpand}
            >
              <p className="text-sm text-gray-600 truncate max-w-[150px]">
                {call.summary}
              </p>
              <span className="text-gray-400 group-hover:text-gray-600 flex-shrink-0">
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </div>

            {/* Popup for expanded summary */}
            {isExpanded && (
              <div className="absolute right-0 top-full z-50 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg w-96 max-w-[90vw]">
                <p className="text-sm text-gray-700 leading-relaxed">{call.summary}</p>
                <button
                  type="button"
                  onClick={onToggleExpand}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              </div>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>

      {/* Note */}
      <NoteCell
        note={call.notes}
        telephonySessionId={call.telephonySessionId}
        onNoteUpdate={onNoteUpdate}
      />
    </tr>
  )
}
